import {ReactElement, useMemo} from "react";
import {MonthlyAverageBasePeriod} from "../../../../../__generated__/generated_types";
import {Account} from "../../../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {parseNumber} from "../../../../../utils/string-helpers";
import {ensureNotObject, formatYearAndVersionType, keyify, renderPossibleArray} from "../common";
import {RenderAuditLogsEnrichment, RendererProps} from "../RendererProps";
import * as common from "../styles/common.module.scss";
import {
    renderAccountPercentageMonthValue,
    renderCustomMonthlyValue,
    renderDistributionMethod,
    renderMonthlyAvgBasePeriod,
    renderOperationalMonthValue
} from "./common";
import * as styles from "./styles/RenderPendingUpdates.module.scss";

function renderLookback(lookback: any): string {
    if(typeof lookback !== "number" || lookback <= 0) {
        return "";
    }

    return `Lookback Period: ${lookback} month${lookback > 1 ? "s" : ""}`;
}

function renderAccountPercentage(accPercentage: Record<string, any>, accountsMap: Record<string, Account>): ReactElement {
    if(!accPercentage) {
        return <></>;
    }

    const accountId = accPercentage["sourceAccountId"];
    if(!accountId) {
        return <></>;
    }

    let name = accountId;
    const account = accountsMap[accountId];
    if(account) {
        if(account.name) {
            name = account.name;
        } else if(account.accountNumber) {
            name = `${account.accountNumber} ${account.name}`;
        }
    }

    const monthlyPercents = accPercentage["monthlyPercents"];
    const monthlyRender: ReactElement[] = [];
    if(Array.isArray(monthlyPercents) && monthlyPercents.length > 0) {
        for(let i = 0; i < monthlyPercents.length; i++) {
            const percentVal = monthlyPercents[i];
            monthlyRender.push(<li>{renderAccountPercentageMonthValue(i + 1, account, percentVal)}</li>);
        }
    }

    const lookback = accPercentage["lookbackPeriod"];
    return <div>
        <div>Source Account: {ensureNotObject(name)}</div>
        {renderAccountPercentageAugments(accPercentage)}
        {typeof lookback === "number" && lookback > 0 ? <div>{renderLookback(lookback)}</div> : <></>}
        {monthlyRender.length > 0 && <ul>{monthlyRender}</ul>}
    </div>;
}

function renderAccountPercentageAugments(accPercentageAugments: Record<string, any>): ReactElement {
    if(!accPercentageAugments) {
        return <></>;
    }

    const minVal = accPercentageAugments["minVal"];
    const maxVal = accPercentageAugments["maxVal"];

    if((minVal === undefined || minVal === null) && (maxVal === undefined || maxVal === null)) {
        return <></>;
    }

    return <>
        <div>Minimum: {ensureNotObject(minVal)}</div>
        <div>Maximum: {ensureNotObject(maxVal)}</div>
    </>;
}

function renderWorksheet(worksheet: Record<string, any>): ReactElement {
    if(!worksheet) {
        return <></>;
    }

    const monthlyValues = worksheet["monthlyValues"];
    const description = worksheet["description"];
    if(typeof description !== "string") {
        return <></>;
    }

    return <li>{description}: {renderPossibleArray(monthlyValues)}</li>;
}

function renderCustom(custom: Record<string, any>): ReactElement {
    if(typeof custom !== "object") {
        return <></>;
    }

    const name = custom["itemName"];
    const monthlyCount = custom["monthlyCount"];
    const monthlyAmounts = custom["monthlyAmounts"];
    const monthlyPercents = custom["monthlyPercents"];

    const monthlyRender: ReactElement[] = [];
    if(
        Array.isArray(monthlyAmounts) &&
        Array.isArray(monthlyPercents) &&
        Array.isArray(monthlyCount) &&
        monthlyAmounts.length == monthlyPercents.length &&
        monthlyCount.length == monthlyAmounts.length
    ) {
        for(let i = 0; i < monthlyAmounts.length; i++) {
            const count = monthlyCount[i];
            const amount = monthlyAmounts[i];
            const percent = monthlyPercents[i];

            if(count !== "0" && amount !== "0" && percent !== "0") {
                monthlyRender.push(<li key={i}>{renderCustomMonthlyValue(i + 1, count, amount, percent)}</li>);
            }
        }
    }

    return <div>
        <div>{name}</div>
        {monthlyRender.length > 0 && <ul>{monthlyRender}</ul>}
    </div>;
}

function renderOperational(operational: Record<string, any>, metricTypes: {id: string, name: string}[]): ReactElement {
    if(!operational) {
        return <></>;
    }

    const sourceId = operational["operationalMetricTypeId"];
    const metricType = metricTypes.find(mt => mt.id === sourceId);
    const lookback = operational["delayRecognition"];
    const name = operational["name"];
    const cumSum = operational["cumSum"];
    const nonProrated = operational["nonProrated"];
    const minValue = operational["minValue"];
    const maxValue = operational["maxValue"];
    const monthlyAmounts = operational["monthlyAmounts"];
    const monthlyPercents = operational["monthlyPercents"];

    const monthRender: ReactElement[] = [];
    if(Array.isArray(monthlyAmounts) && Array.isArray(monthlyPercents) && monthlyAmounts.length == monthlyPercents.length) {
        for(let i = 0; i < monthlyAmounts.length; i++) {
            const monthlyAmount = monthlyAmounts[i];
            const monthlyPercent = monthlyPercents[i];
            monthRender.push(<li key={i}>{renderOperationalMonthValue(i + 1, metricType, monthlyPercent, monthlyAmount)}</li>);
        }
    }

    const minValueAsNumOrNull = parseNumber(minValue);
    const maxValueAsNumOrNull = parseNumber(maxValue);

    return <div>
        {name && <div>Name: {ensureNotObject(name)}</div>}
        {metricType && <div>Source Metric: {ensureNotObject(metricType.name)}</div>}
        {(typeof lookback === "number" && lookback > 0) ? <div>{renderLookback(lookback)}</div> : <></>}
        {cumSum && <div>Has Ramp Up</div>}
        {nonProrated === true && <div>Not Prorated</div>}
        <div>Min Value: {minValueAsNumOrNull ?? "None"}</div>
        <div>Max Value: {maxValueAsNumOrNull ?? "None"}</div>
        {monthRender.length > 0 && <ul>{monthRender}</ul>}
    </div>;
}

function renderPayroll(payroll: Record<string, any>[], positionMap: Record<string, {id: string, name: string}>): ReactElement {
    if(!payroll || payroll.length === 0) {
        return <></>;
    }

    const primaryCollected: Record<string, any[]> = {};
    const itemsCollected: Record<string, any[]> = {};
    for(const p of payroll) {
        const positionId = p["positionId"];
        const primaryCompItem = p["primaryCompensationItem"];
        const compItemId = p["compensationItemId"];

        if(!positionId) {
            continue;
        }

        if(primaryCompItem) {
            const positionIds = primaryCollected[primaryCompItem];
            if(!positionIds) {
                primaryCollected[primaryCompItem] = [positionId];
            } else {
                positionIds.push(positionId);
            }
        }

        if(compItemId) {
            const positionIds = itemsCollected[compItemId];
            if(!positionIds) {
                itemsCollected[compItemId] = [positionId];
            } else {
                positionIds.push(positionId);
            }
        }
    }

    const items: ReactElement[] = [];
    for(const [primaryCompItem, positionIds] of Object.entries(primaryCollected)) {
        const positionNames = positionIds.map(n => positionMap[n]?.name ?? "UNKONWN");
        items.push(
            <div>
                <div>Type: {ensureNotObject(primaryCompItem)}</div>
                {positionNames.length > 0 &&
                    <ul>
                        {positionNames.map(n => <li key={keyify(n)}>{ensureNotObject(n)}</li>)}
                    </ul>
                }
            </div>
        );
    }

    for(const [compItemId, positionIds] of Object.entries(itemsCollected)) {
        items.push(
                <div>
                    <div>Type: {ensureNotObject(compItemId)}</div>
                    {positionIds.length > 0 &&
                        <ul>
                            {positionIds.map(n => <li key={keyify(n)}>{ensureNotObject(n)}</li>)}
                        </ul>
                    }
                </div>
        );
    }

    if(items.length === 0) {
        return <></>;
    }

    return <div>
        {items}
    </div>;
}

function renderPercentGrowth(percentGrowth: Record<string, any>): ReactElement {
    if(!percentGrowth) {
        return <></>;
    }

    const growth = percentGrowth["percentGrowthAdjustmentValue"];
    const basePeriod = percentGrowth["percentGrowthBasePeriod"];

    if(!basePeriod) {
        return <></>;
    }

    return <div>
        <div>Growth: {ensureNotObject(growth)}%</div>
        <div>Base Period: {ensureNotObject(basePeriod)}</div>
    </div>;
}

function renderYearlyAverage(yearlyAverage: Record<string, any>): ReactElement {
    if(!yearlyAverage) {
        return <></>;
    }

    const monthlyAdjustmentType = yearlyAverage["monthlyAdjustmentType"];
    const monthlyAdjustmentValue = yearlyAverage["monthlyAdjustmentValue"];
    const periodBase = yearlyAverage["monthlyAverageBasePeriod"];

    if(!monthlyAdjustmentType || !periodBase) {
        return <></>;
    }

    const customPeriod = MonthlyAverageBasePeriod.CustomLookbackPeriod === periodBase;
    const periodEnd = yearlyAverage["lookbackPeriodEnd"];
    const periodStart = yearlyAverage["lookbackPeriodStart"];

    return <div>
        <div>Yearly Adjustment: {ensureNotObject(monthlyAdjustmentValue)} {ensureNotObject(monthlyAdjustmentType)}</div>
        <div>
            {renderMonthlyAvgBasePeriod(periodBase)}
            {customPeriod && periodStart && <div className={styles.indentedChild}>From: {ensureNotObject(periodStart)}</div>}
            {customPeriod && periodEnd && <div className={styles.indentedChild}>To: {ensureNotObject(periodEnd)}</div>}
        </div>
    </div>;
}

function renderMonthlyAverage(monthlyAverage: Record<string, any>): ReactElement {
    if(!monthlyAverage) {
        return <></>;
    }

    const monthlyAdjustmentType = monthlyAverage["monthlyAdjustmentType"];
    const monthlyAdjustmentValue = monthlyAverage["monthlyAdjustmentValue"];
    const periodBase = monthlyAverage["monthlyAverageBasePeriod"];

    if(!monthlyAdjustmentType || !periodBase) {
        return <></>;
    }

    const customPeriod = MonthlyAverageBasePeriod.CustomLookbackPeriod === periodBase;
    const periodEnd = monthlyAverage["lookbackPeriodEnd"];
    const periodStart = monthlyAverage["lookbackPeriodStart"];

    return <div>
        <div>Monthly Adjustment: {ensureNotObject(monthlyAdjustmentValue)} {ensureNotObject(monthlyAdjustmentType)}</div>
        <div>
            {renderMonthlyAvgBasePeriod(periodBase)}
            {customPeriod && periodStart && <div className={styles.indentedChild}>From: {ensureNotObject(periodStart)}</div>}
            {customPeriod && periodEnd && <div className={styles.indentedChild}>To: {ensureNotObject(periodEnd)}</div>}
        </div>
    </div>;
}

function renderAnnualTargetValue(annualAmount: Record<string, any>): ReactElement {
    if(!annualAmount) {
        return <></>;
    }

    const amount = annualAmount["annualTargetValue"];
    const distributionMethod = annualAmount["distributionMethod"];

    if(amount === null || amount === undefined || !distributionMethod) {
        return <></>;
    }

    return <div>
        <div>Annual Amount: {amount}</div>
        <div>Distribution Method: {renderDistributionMethod(distributionMethod)}</div>
    </div>;
}

function renderOperationalMonthlyAverage(operationalMonthlyAverage: Record<string, any>): ReactElement {
    if(!operationalMonthlyAverage) {
        return <></>;
    }

    const sourceMetric = operationalMonthlyAverage["operationalMetric"];
    const startMonth = operationalMonthlyAverage["startMonth"];
    const endMonth = operationalMonthlyAverage["endMonth"];
    const monthlyGrowth = operationalMonthlyAverage["monthlyGrowth"];
    const yearlyGrowth = operationalMonthlyAverage["yearlyGrowth"];
    const customIncrease = operationalMonthlyAverage["customIncrease"];
    const customIncreases = operationalMonthlyAverage["customIncreases"];

    if(!sourceMetric || !startMonth || !endMonth) {
        return <></>;
    }

    return <div>
        <div>Source Metric: {ensureNotObject(sourceMetric)}</div>
        <div>Start Month: {ensureNotObject(startMonth)}</div>
        <div>End Month: {ensureNotObject(endMonth)}</div>
        {monthlyGrowth ? <div>Monthly Growth: {ensureNotObject(monthlyGrowth)}%</div> : <></>}
        {yearlyGrowth ? <div>Yearly Growth: {ensureNotObject(yearlyGrowth)}%</div> : <></>}
        {customIncrease ? <div>Custom Growth: {renderPossibleArray(customIncreases)}</div> : <></>}
        {!monthlyGrowth && !yearlyGrowth && !customIncrease ? <div>No Growth</div> : <></>}
    </div>;
}

function renderDrivers(
        drivers: Record<string, any>,
        accountsMap: Record<string, Account>,
        sourceMetrics: {id: string, name: string}[],
        payrollPositionMap: Record<string, {id: string, name: string}>
): ReactElement[] {
    if(!drivers) {
        return [];
    }

    const accounts = drivers["accountPercentage"];
    const payrollCompensation = drivers["payrollCompensation"];
    const payrollItems = drivers["payrollCompensationItem"];
    const worksheet = drivers["lineItems"];
    const customItems = drivers["custom"];
    const operational = drivers["operational"];
    const growth = drivers["growth"];
    const operationalMonthlyAverage = drivers["operationalMonthlyAverage"];

    let payroll: Record<string, any>[] | null = null;
    if(Array.isArray(payrollCompensation)) {
        payroll = payrollCompensation;
    }
    if(Array.isArray(payrollItems)) {
        if(payroll !== null) {
            payroll = payroll.concat(payrollItems);
        } else {
            payroll = payrollItems;
        }
    }

    const renders: ReactElement[] = [];
    if(Array.isArray(accounts) && accounts.length > 0) {
        renders.push(<div className={common.section}>
            <div>Account Percentage</div>
            <div className={styles.driverData}>{accounts.map(a => renderAccountPercentage(a, accountsMap))}</div>
        </div>);
    }

    if(Array.isArray(payroll) && payroll.length > 0) {
        renders.push(<div className={common.section}>
            <div>Payroll</div>
            <div className={styles.driverData}>{renderPayroll(payroll, payrollPositionMap)}</div>
        </div>);
    }

    if(Array.isArray(worksheet) && worksheet.length > 0) {
        renders.push(<div className={common.section}>
            <div>Line Items</div>
            <ul>{worksheet.map(w => renderWorksheet(w))}</ul>
        </div>);
    }

    if(Array.isArray(customItems) && customItems.length > 0) {
        renders.push(<div className={common.section}>
            <div>Custom</div>
            <div className={styles.driverData}>{customItems.map(c => renderCustom(c))}</div>
        </div>);
    }

    if(Array.isArray(operational) && operational.length > 0) {
        renders.push(<div className={common.section}>
            <div>Operational</div>
            <div className={styles.driverData}>{operational.map(o => renderOperational(o, sourceMetrics))}</div>
        </div>);
    }

    if(typeof growth === "object" && growth) {
        const percentGrowth = growth["percentGrowth"];
        const yearlyAverage = growth["yearlyAverage"];
        const monthlyAverage = growth["monthlyAverage"];
        const annualTargetValue = growth["annualTargetValue"];

        if(typeof percentGrowth === "object" && percentGrowth) {
            // There is only ever one percent growth driver, but
            // for some reason we decided to use an array. :/
            renders.push(<div className={common.section}>
                <div>Percent Growth</div>
                <div className={styles.driverData}>{renderPercentGrowth(percentGrowth)}</div>
            </div>);
        }

        if(typeof yearlyAverage === "object" && yearlyAverage) {
            // There is only ever one yearly average driver, but
            // for some reason we decided to use an array. :/
            renders.push(<div className={common.section}>
                <div>Yearly Average</div>
                <div className={styles.driverData}>{renderYearlyAverage(yearlyAverage)}</div>
            </div>);
        }

        if(typeof monthlyAverage === "object" && monthlyAverage) {
            // There is only ever one monthly average driver, but
            // for some reason we decided to use an array. :/
            renders.push(<div className={common.section}>
                <div>Monthly Average</div>
                <div className={styles.driverData}>{renderMonthlyAverage(monthlyAverage)}</div>
            </div>);
        }

        if(typeof annualTargetValue === "object" && annualTargetValue) {
            // There is only ever one annual target value driver, but
            // for some reason we decided to use an array. :/
            renders.push(<div className={common.section}>
                <div>Annual Target Value</div>
                <div className={styles.driverData}>{renderAnnualTargetValue(annualTargetValue)}</div>
            </div>);
        }
    }

    if(typeof operationalMonthlyAverage === "object" && operationalMonthlyAverage) {
        renders.push(<div className={common.section}>
            <div>Operational Monthly Average</div>
            <div className={styles.driverData}>{renderOperationalMonthlyAverage(operationalMonthlyAverage)}</div>
        </div>);
    }

    return renders;
}

export interface RenderBulkAddDriversProps extends RendererProps {
    accounts: Account[];
    operationalMetricTypes: {id: string, name: string}[];
    enrichment?: RenderAuditLogsEnrichment;
}

export function RenderBulkAddDrivers(props: RenderBulkAddDriversProps): ReactElement {
    const accountMap = useMemo(() => {
        return props.accounts.toIdMap(a => a.id);
    }, [props.accounts]);

    const payrollPositionMap = useMemo(() => {
        if(props.enrichment?.payrollPositions) {
            return props.enrichment.payrollPositions.toIdMap(p => p.id);
        }
        return {};
    }, [props.enrichment]);

    const versionType = props.variables["versionType"];
    const year = props.variables["year"];

    return <div>
        <div className={common.textHeader}>{formatYearAndVersionType(year, versionType)}</div>
        <div>{renderDrivers(props.variables, accountMap, props.operationalMetricTypes, payrollPositionMap)}</div>
    </div>;
}